import classNames from 'classnames';
import { TemplateClassNames } from "../../../../types/template-layouts";
import { BlProfile, BrandliveEvent, HomepageVideo, LanguagesAbbr, Session, SessionTypesEnum, SessionTypesFeatures } from "../../../../types/working-model";
import { /*dateCountdown,*/ niceDateTime } from "../../../../utils/utils";
import { Button } from "../../../general-ui/button/button";
import { getAsset } from "../../modules/assets/asset-map";
import './session-stream.scss';
import Video from "../../modules/video/video";
import { useSocket } from "../../../../connection/socket";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useLive from "../../../../utils/use-live";
import { throttle } from "underscore";
import LiveChat from '../live-chat/live-chat';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { useScreenMediaQuery } from '../../../../utils/use-screen-media-query';
import AddToCalendarModal from '../../modules/agenda/add-to-calendar-modal/add-to-calendar-modal';
import { useTranslation } from 'react-i18next';
import Icon, { COLORS, ICONS } from '../../../general-ui/icon';
import SessionCountdown from './session-countdown';
interface SessionStreamProps {
	session: Session;
	eventBundle: BrandliveEvent;
	isEditor?: boolean;
	playback?: string;
	language?: LanguagesAbbr;
	editorSize?: string;
	isAdmin?: boolean;
}

declare global {
	interface Document {
		mozCancelFullScreen?: () => Promise<void>;
		msExitFullscreen?: () => Promise<void>;
		webkitExitFullscreen?: () => Promise<void>;
		mozFullScreenElement?: Element;
		msFullscreenElement?: Element;
		webkitFullscreenElement?: Element;
	}

	interface HTMLElement {
		msRequestFullscreen?: () => Promise<void>;
		mozRequestFullscreen?: () => Promise<void>;
		webkitRequestFullscreen?: () => Promise<void>;
	}
}

const isMobile = 'ontouchstart' in document.documentElement;

const SessionStream: React.FC<SessionStreamProps> = ({
	session,
	eventBundle,
	isEditor,
	playback = null,
	language = 'en',
	editorSize = '',
	isAdmin = false,
}) => {
	const template = TemplateClassNames[eventBundle.template.name];
	const startDate = SessionTypesFeatures[session.session_type].has_start_time ? niceDateTime(session!.timestamp!) : null;
	const socket = useSocket(`session-${session.uuid}-${language}`);
	const [playbackUrl, setPlaybackUrl] = useState<string | null>(playback);
	const [currentlyWatching, setCurrentlyWatching] = useState<string | null>(null);
	const isLive = useLive(playbackUrl);
	const scrollContainer = useRef<HTMLDivElement | null>(null);
	const [hasScrolledPast, setHasScrolledPast] = useState(false);
	const [openAddToCalendar, setOpenAddToCalendar] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [chatClosed, setChatClosed] = useState(false);
	const container = useRef<HTMLDivElement | null>(null);
	const { t } = useTranslation("session");
	// const countdown = useSessionCountdown(session.timestamp);
	const {
		isLessThan1024,
		isLessThan640
	} = useScreenMediaQuery();

	const {
		LiveEventReducer: {
			loadingEventBundle, // TODO: move to props
			blProfileUser, // TODO: move to props
		},
	} = useTypedSelector(state => state);

	useEffect(() => {
		const playerUrlListener = (data: any) => {
			setPlaybackUrl(data);
		};

		const currentlyOnPage = (data: { viewers: string | null }) => {
			setCurrentlyWatching(data.viewers);
		};

		socket.addListener('playbackUrl', playerUrlListener);
		socket.addListener('on-page', currentlyOnPage);

		//get currently watching on load
		socket.getCurrentlyWatching(({ viewers }) => {
			setCurrentlyWatching(viewers);
		});

		return () => {
			socket.removeListener('playbackUrl', playerUrlListener);
			socket.removeListener('on-page', currentlyOnPage);
		};
	}, [socket]);

	useEffect(() => {
		if (playback) setPlaybackUrl(playback);
	}, [playback]);

	const styleOverrides = useMemo(() => {

		if (session.video?.customizations?.backgroundColor) {
			return { backgroundColor: `var(--${session.video?.customizations?.backgroundColor})`, padding: isEditor ? 0 : undefined };
		}

		if (session.video?.customizations?.backgroundImage) {
			return { backgroundImage: `url(${session.video?.customizations?.backgroundImage})` };
		}

		return {};
	}, [session, isEditor]);

	const placeholderStyle = useMemo(() => {
		const isFuture = session.timestamp && session.timestamp > Date.now();

		if (session.post_broadcast_thumbnail && !isFuture) {
			return { backgroundImage: `url(${session.post_broadcast_thumbnail})` };
		} else if (session.pre_broadcast_thumbnail) {
			return { backgroundImage: `url(${session.pre_broadcast_thumbnail})` };
		} else {
			return { backgroundImage: `url(${getAsset(eventBundle?.template.name, 'video_background')})` };
		}
	}, [session, eventBundle]);

	useEffect(() => {
		const handleScroll = throttle(() => {
			const rect = scrollContainer.current?.getBoundingClientRect();

			if (rect) {
				const scrolledPast = Math.abs(rect.y) > rect.height;
				if (scrolledPast !== hasScrolledPast) {
					setHasScrolledPast(scrolledPast);
				}
			}
		}, 500);

		if (scrollContainer.current) {
			window.addEventListener('scroll', handleScroll);
		}

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [scrollContainer, hasScrolledPast]);

	const closeAddToCalendar = useCallback(() => {
		setOpenAddToCalendar(false);
	}, []);

	const openAddToCalendarModal = useCallback(() => {
		setOpenAddToCalendar(true);
	}, []);

	const forceOverlay = useMemo(() => editorSize === 'tablet' || (isLessThan1024 && !isLessThan640), [isLessThan1024, isLessThan640, editorSize]);
	const mobileOnly = isLessThan640 || editorSize === 'mobile';

	const didEnterFullscreen = useCallback(() => {
		setIsFullscreen(true);
	}, []);

	const errorEnterFullscreen = useCallback((e: any) => {
		setIsFullscreen(false);
		console.error(e);
	}, []);

	const didExitFullscreen = useCallback(() => {
		setIsFullscreen(false);
		setHasScrolledPast(false);
	}, []);

	useEffect(() => {
		const _container = container.current;
		const handleFullscreenChange = () => {
			if (document.fullscreenElement || document.msFullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement) {
				setIsFullscreen(true);
			} else {
				setIsFullscreen(false);
			}
		};

		_container?.addEventListener('fullscreenchange', handleFullscreenChange);
		_container?.addEventListener('webkit-fullscreenchange', handleFullscreenChange);

		return () => {
			_container?.removeEventListener('fullscreenchange', handleFullscreenChange);
			_container?.removeEventListener('webkit-fullscreenchange', handleFullscreenChange);
		};
	}, [isFullscreen]);

	/**
	 * Browsers haven't fully adopted the requestFullscreen API.
	 * 
	 * Need to polyfill a bunch here.
	 */
	const handleFullscreen = useCallback((fullscreen: boolean) => {
		if (fullscreen) {
			if (container.current?.requestFullscreen) {
				container.current?.requestFullscreen()
					.then(didEnterFullscreen)
					.catch(errorEnterFullscreen);
			} else if (container.current?.webkitRequestFullscreen) {
				container.current?.webkitRequestFullscreen();
				didEnterFullscreen();
			} else if (container.current?.msRequestFullscreen) {
				container.current?.msRequestFullscreen()
					.then(didEnterFullscreen)
					.catch(errorEnterFullscreen);
			} else if (container.current?.mozRequestFullscreen) {
				container.current?.mozRequestFullscreen()
					.then(didEnterFullscreen)
					.catch(errorEnterFullscreen);
			}
		} else {
			if (document.exitFullscreen) {
				document.exitFullscreen()
					.then(didExitFullscreen)
					.catch(errorEnterFullscreen);
			} else if (document.webkitExitFullscreen) {
				document.webkitExitFullscreen();
				didExitFullscreen();
			} else if (document.mozCancelFullScreen) {
				document.mozCancelFullScreen()
					.then(didExitFullscreen)
					.catch(errorEnterFullscreen);
			} else if (document.msExitFullscreen) {
				document.msExitFullscreen()
					.then(didExitFullscreen)
					.catch(errorEnterFullscreen);
			}
		}
	}, [didEnterFullscreen, didExitFullscreen, errorEnterFullscreen]);


	const handleChatClose = useCallback((chatClosed: boolean) => {
		setChatClosed(chatClosed);
	}, []);

	const toggleChat = useCallback(() => {
		setChatClosed(!chatClosed);
	}, [chatClosed]);

	return (
		<div className={classNames("session-stream-wrapper", template, { 'chat-closed': chatClosed, 'overlay-chat': session.layout?.overlayChat || forceOverlay })} style={styleOverrides} ref={scrollContainer}>
			<div className={'session-stream-container'}>
				<div className={classNames("session-stream-container-inner", { center: session.layout?.overlayChat || forceOverlay })}>
					<div className={classNames("session-stream")} ref={container}>
						{session.session_type === 'on-demand' && session.on_demand_video ? (
							<div className={classNames("session-video-player-container", { "scrolled-past": hasScrolledPast && !isEditor && !isMobile && !isFullscreen })}>
								<Video
									video={session.on_demand_video as HomepageVideo}
									template={template}
									main={true}
									onRequestFullscreen={handleFullscreen}
									isFullscreen={isFullscreen}
									onChatClose={handleChatClose}
									chatClosed={chatClosed}
									hideChatButton={!session.layout?.overlayChat && (isFullscreen && !isLessThan1024)}
									isOnDemand={session.session_type === SessionTypesEnum.onDemand}
									currentlyWatching={currentlyWatching}
								/>
							</div>
						) : (
							<>
								{
									isLive ? (
										<div className={classNames("session-video-player-container", { "scrolled-past": hasScrolledPast && !isEditor && !isMobile && !isFullscreen })}>
											<Video
												url={playbackUrl!}
												template={template}
												main={true}
												onRequestFullscreen={handleFullscreen}
												isFullscreen={isFullscreen}
												onChatClose={handleChatClose}
												chatClosed={chatClosed}
												hideChatButton={!session.layout?.overlayChat && (isFullscreen && !isLessThan1024)}
												currentlyWatching={currentlyWatching}
											/>
										</div>
									) : (
										<div className="session-video-placeholder" style={placeholderStyle}>
											{session.session_type !== 'on-demand' && (
												<div className="live-in-card">
													<div className="live-in-card-text">
														<SessionCountdown timestamp={session.timestamp} />
														<p>{startDate}</p>
													</div>
													<Button
														onClick={openAddToCalendarModal}
														classButton="primary"
														template={template}
													>{t("Add to Calendar")}</Button>
												</div>
											)}
											<Button
												onClick={toggleChat}
												classButton={classNames('player-button chat-close waiting')}
												template={template}
											>
												{
													chatClosed ? (
														<Icon name={ICONS.CHAT_COMMENT} size={22} color={COLORS.WHITE} />
													) : (
														<Icon name={ICONS.CHAT_HIDE} size={22} color={COLORS.WHITE} />
													)
												}
											</Button>
										</div>

									)}

							</>
						)}

						{//for tablet, we force the overlay regarless of what the admin has selected
							((session.layout?.overlayChat || forceOverlay) && !mobileOnly) && (
								<LiveChat
									session={session}
									loading={!!loadingEventBundle}
									language={language}
									registrationOn={!!eventBundle?.registration_on}
									blProfileUser={blProfileUser as BlProfile}
									template={template}
									forceOverlay={true}
									mobileOnly={false}
									onChatClose={handleChatClose}
									chatClosed={chatClosed}
									eventBundle={eventBundle}
									isAdmin={isAdmin}
								/>
							)
						}
					</div>

				</div>
				{//necessary to break these up to get the layouts correct
					(!session.layout?.overlayChat && !forceOverlay && !isFullscreen) && !mobileOnly && (
						<LiveChat
							session={session}
							loading={!!loadingEventBundle}
							language={language}
							registrationOn={!!eventBundle?.registration_on}
							blProfileUser={blProfileUser as BlProfile}
							template={template}
							forceOverlay={false}
							mobileOnly={false}
							onChatClose={handleChatClose}
							chatClosed={chatClosed}
							eventBundle={eventBundle}
						/>
					)
				}
			</div>

			<AddToCalendarModal
				session={session}
				open={openAddToCalendar}
				close={closeAddToCalendar}
				language={language}
				isSingleSessionNoHomepage={eventBundle?.sessions?.length === 1 && !eventBundle?.homepage}
			/>
		</div>
	);
};

export default SessionStream;